// Shared plain-text-with-clickable-links rendering.
//
// Messages across the cockpit are stored and displayed as plain text (there is no markdown
// renderer), so any URL a rep writes would otherwise be dead characters. These two pieces turn
// them into real anchors: <MessageLink/> is the anchor itself, <LinkedText/> splices one in for
// every link found in a string. ChatDock composes MessageLink into its own richer MessageText
// (which also handles file-path download buttons); simpler surfaces use LinkedText directly.
import React, { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { detectLinks } from '../utils/detectLinks';

interface MessageLinkProps {
  href: string;
  label: string;
  // Override for panes that aren't on the normal page background (e.g. the dark output <pre>),
  // where the default link blue doesn't have enough contrast.
  color?: string;
}

export const MessageLink: React.FC<MessageLinkProps> = ({ href, label, color }) => {
  const { theme } = useTheme();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // Don't let the click reach a container that treats clicks as expand/collapse or select.
      onClick={(e) => e.stopPropagation()}
      title={href}
      style={{ color: color || theme.primary, textDecoration: 'underline', wordBreak: 'break-word' }}
    >
      {label}
    </a>
  );
};

interface LinkedTextProps {
  text: string | null | undefined;
  color?: string;
}

export const LinkedText: React.FC<LinkedTextProps> = ({ text, color }) => {
  const content = text || '';
  const links = useMemo(() => detectLinks(content), [content]);
  if (links.length === 0) return <>{content}</>;

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  links.forEach((m, i) => {
    if (m.start > cursor) nodes.push(content.slice(cursor, m.start));
    nodes.push(<MessageLink key={`${m.start}-${i}`} href={m.href} label={m.label} color={color} />);
    cursor = m.end;
  });
  if (cursor < content.length) nodes.push(content.slice(cursor));
  return <>{nodes}</>;
};
